import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import api from "../../services/api";

const getDependenciesOf = (rows, id) => {
  const row = rows.find((r) => String(r.id_pai) === String(id));
  return (row?.id_filhos || []).map(String);
};

/**
 * Modal para gerenciar dependências entre épicos/histórias de usuário
 * Permite adicionar/remover dependências de um épico/histórias de usuário
 */
function ModalManageDependencies({
  isOpen,
  onClose,
  epicId,
  epicName,
  allTasks,
  currentDependencies = [],
  onSuccess,
}) {
  const [setLoadingDeps] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dependencies, setDependencies] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [selectedNewDependency, setSelectedNewDependency] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Inicializa as dependências e tarefas disponíveis
  useEffect(() => {
    if (!isOpen || !epicId || !allTasks) return;

    // Filtra as tarefas que não são do item atual
    const available = allTasks.filter(
      (task) => String(task.id) !== String(epicId)
    );

    const validAvailableTasks = available.filter(
      (task) => task.name && task.name.trim() !== ""
    );

    const filteredOptions = validAvailableTasks.filter(
      (task) =>
        !dependencies.includes(String(task.id)) &&
        String(task.id) !== String(epicId)
    );

    setAvailableTasks(filteredOptions);

    const fetchDependencies = async () =>  {
      setLoadingDeps(true);
      setError(null);
      try {
        const { data } = await api.get("/dependencias");

        // Encontra a linha cujo id_pai é o item atual
        setDependencies(getDependenciesOf(data, epicId));
      } catch (error) {
        console.error("Erro ao buscar dependências:", error);
        // Fallback: usa o que já veio via props, caso a API falhe
        setDependencies((currentDependencies || []).map(String));
        setError("Não foi possível atualizar as depndências. Exibindo dados em cache.");
      } finally {
        setLoadingDeps(false);
      }
    };

    fetchDependencies();
    setSelectedNewDependency("");
    setSuccess(null);
  }, [isOpen, epicId, allTasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDependencies = useCallback(async () => {
    try {
      const { data } = await api.get("/dependencias");
      setDependencies(getDependenciesOf(data, epicId));
    } catch (error) {
      console.error("Erro ao buscar dependências:", error);
    }
  }, [epicId]);

const handleAddDependency = useCallback(async () => {
  if (!selectedNewDependency) {
    setError("Selecione um item para adicionar como dependência");
    return;
  }
  if (dependencies.includes(String(selectedNewDependency))) {
    setError("Este item já é uma dependência");
    return;
  }

  try {
    setLoading(true);
    setError(null);

    await api.patch(`/atualizarDependencias/${epicId}`, {
      dependencies: [selectedNewDependency],
    });

    await fetchDependencies();
    setSelectedNewDependency("");
    setSuccess("Dependência adicionada com sucesso!");
    setTimeout(() => setSuccess(null), 3000);

    if (onSuccess) onSuccess();
  } catch (err) {
    console.error("Erro ao adicionar dependência:", err);
    setError(
      err.response?.data?.error || "Erro ao adicionar dependência. Tente novamente."
    );
  } finally {
    setLoading(false);
  }
}, [selectedNewDependency, dependencies, epicId, onSuccess, fetchDependencies]);

const handleRemoveDependency = useCallback(
  async (depId) => {
    try {
      setLoading(true);
      setError(null);

      await api.delete(`/atualizarDependencias/${epicId}`, {
        data: { dependencies: [depId] },
      });

      await fetchDependencies();
      setSuccess("Dependência removida com sucesso!");
      setTimeout(() => setSuccess(null), 3000);

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Erro ao remover dependência:", err);
      setError(
        err.response?.data?.error || "Erro ao remover dependência. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  },
  [epicId, onSuccess, fetchDependencies]
);

  if (!isOpen) return null;

  // Busca nomes das dependências atuais
  const currentDepTasks = dependencies
    .map((depId) => allTasks.find((t) => String(t.id) === String(depId)))
    .filter(Boolean);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: 12,
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
          padding: 24,
          maxWidth: 500,
          maxHeight: "80vh",
          overflowY: "auto",
          overflowX: "hidden",
          width: "90%",
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ margin: "0 0 8px 0", color: "#1D4ED8", fontSize: 18 }}>
            Gerenciar Dependências
          </h2>
          <p style={{ margin: 0, color: "#6B7280", fontSize: 13 }}>
            {epicName}
          </p>
        </div>

        {/* Mensagens */}
        {error && (
          <div
            style={{
              padding: 12,
              background: "#FEE2E2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              color: "#DC2626",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              padding: 12,
              background: "#DCFCE7",
              border: "1px solid #86EFAC",
              borderRadius: 8,
              color: "#16A34A",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {success}
          </div>
        )}

        {/* Seção: Adicionar Predecessora */}
        <div style={{ marginBottom: 24 }}>
          <h3
            style={{
              margin: "0 0 12px 0",
              fontSize: 14,
              fontWeight: 600,
              color: "#374151",
            }}
          >
            Adicionar Predecessora
          </h3>

          <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
            <Select 
              options={availableTasks.map(task => ({
                value: task.id,
                label: task.name,
              }))}
              value={
                availableTasks
                  .map(task => ({ value: task.id, label: task.name }))
                  .find(option => option.value === selectedNewDependency)
              }
              onChange={(option) => setSelectedNewDependency(option.value)}
              menuPlacement="auto"
              maxMenuHeight={250}
              styles={{
                container: (base) => ({
                  ...base,
                  width: 250,
                })
              }}
            />

            <button
              onClick={handleAddDependency}
              disabled={loading || !selectedNewDependency}
              style={{
                padding: "8px 16px",
                background:
                  loading || !selectedNewDependency ? "#D1D5DB" : "#16A34A",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                cursor:
                  loading || !selectedNewDependency ? "not-allowed" : "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                if (!loading && selectedNewDependency) {
                  e.currentTarget.style.background = "#15803D";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && selectedNewDependency) {
                  e.currentTarget.style.background = "#16A34A";
                }
              }}
            >
              {loading ? "..." : "+ Adicionar"}
            </button>
          </div>
        </div>

        {/* Seção: Dependências Atuais */}
        <div style={{ marginBottom: 24 }}>
          <h3
            style={{
              margin: "0 0 12px 0",
              fontSize: 14,
              fontWeight: 600,
              color: "#374151",
            }}
          >
            Dependências Atuais
            <span style={{ color: "#6B7280", fontWeight: 400, marginLeft: 8 }}>
              ({dependencies.length})
            </span>
          </h3>

          {dependencies.length === 0 ? (
            <div
              style={{
                padding: 12,
                background: "#F3F4F6",
                borderRadius: 6,
                color: "#6B7280",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              Nenhuma dependência. Este item não depende de outros.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {currentDepTasks.map((task) => (
                <div
                  key={task.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: 12,
                    background: "#F9FAFB",
                    border: "1px solid #E5E7EB",
                    borderRadius: 6,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      minWidth: 0,
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ flexShrink: 0 }}
                    >
                      <path
                        d="M8 1.5C4.13623 1.5 1 4.63623 1 8.5C1 12.3638 4.13623 15.5 8 15.5C11.8638 15.5 15 12.3638 15 8.5C15 4.63623 11.8638 1.5 8 1.5ZM11 8.5C11 9.32843 10.3284 10 9.5 10C8.67157 10 8 9.32843 8 8.5C8 7.67157 8.67157 7 9.5 7C10.3284 7 11 7.67157 11 8.5ZM5.5 10C4.67157 10 4 9.32843 4 8.5C4 7.67157 4.67157 7 5.5 7C6.32843 7 7 7.67157 7 8.5C7 9.32843 6.32843 10 5.5 10Z"
                        fill="#6366F1"
                      />
                    </svg>
                    <span
                      style={{
                        color: "#111827",
                        fontSize: 13,
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={task.name}
                    >
                      {task.name}
                    </span>
                  </div>

                  <button
                    onClick={() => handleRemoveDependency(task.id)}
                    disabled={loading}
                    style={{
                      padding: "4px 8px",
                      background: "#FEE2E2",
                      color: "#DC2626",
                      border: "none",
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: loading ? "not-allowed" : "pointer",
                      transition: "background 0.15s",
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) e.currentTarget.style.background = "#FECACA";
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) e.currentTarget.style.background = "#FEE2E2";
                    }}
                  >
                    Remover
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div
          style={{
            padding: 12,
            background: "#EFF6FF",
            border: "1px solid #BFDBFE",
            borderRadius: 6,
            fontSize: 12,
            color: "#1E40AF",
            marginBottom: 16,
          }}
        >
          <strong>Dica:</strong> Quando você arrasta um item que possui dependências, as tarefas
          dependentes se movem automaticamente.
        </div>

        {/* Botão Fechar */}
        <button
          onClick={onClose}
          style={{
            width: "100%",
            padding: 10,
            background: "#1D4ED8",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#1E40AF")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#1D4ED8")}
        >
          Fechar
        </button>
      </div>
    </div>
  );
}

export default ModalManageDependencies;
